async function ttLoadGarrConfigurationRows(url) {
  let lastError;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const response = await fetch(url, { signal: AbortSignal.timeout(10000) });
      if (!response.ok) throw new Error('GARR configuration data failed to load');
      const rows = await response.json();
      if (!Array.isArray(rows) || !rows.length) throw new Error('GARR configuration data is empty');
      return rows;
    } catch (error) {
      lastError = error;
      if (attempt < 2) await new Promise(resolve => setTimeout(resolve, 250 * (attempt + 1)));
    }
  }
  throw lastError;
}

function ttChooseGarrConfiguration(rows, values, changedIndex) {
  const candidates = rows.filter(row => values.slice(0, changedIndex + 1).every((value, index) => row.options[index] === value));
  return candidates.reduce((best, row) => {
    const score = row.options.reduce((sum, value, index) => sum + (index > changedIndex && value === values[index] ? 1 : 0), 0);
    return !best || score > best.score ? { row, score } : best;
  }, null)?.row;
}

if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
      }

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select:not([data-garr-attribute]), fieldset input:checked')).map(element => element.tagName === 'SELECT' ? element.selectedOptions[0] : element).map(
          ({ dataset }) => dataset.optionValueId
        );
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      connectedCallback() {
        this.garrReady = this.dataset.garrConfigurations ? this.initializeGarrConfigurations().catch(() => { this.garrRows = null; this.closest('product-info')?.resetProductFormState(); this.setAttribute('aria-invalid', 'true'); }) : Promise.resolve();
        this.addEventListener('change', async (event) => {
          let selectedVariantId;
          if (this.dataset.garrConfigurations) {
            const requestedValues = this.optionValues;
            const requestedGroup = event.target.closest('.product-form__input');
            const revision = this.garrRevision = (this.garrRevision || 0) + 1;
            try { await this.garrReady; } catch { return; }
            if (revision !== this.garrRevision || !this.isConnected || !this.garrRows) return;
            const groups = this.optionGroups;
            const index = groups.indexOf(requestedGroup);
            if (index < 0) return;
            const row = ttChooseGarrConfiguration(this.garrRows, requestedValues, index);
            if (!row) return;
            this.applyGarrConfiguration(row, false);
            selectedVariantId = row.id;
          }
          const target = this.getInputForEventTarget(event.target);
          this.updateSelectedSwatchValue(event);
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event,
              target,
              selectedOptionValues: this.selectedOptionValues,
              selectedVariantId,
            },
          });
        });
      }

      get optionGroups() {
        return Array.from(this.querySelectorAll('.product-form__input'));
      }

      get optionValues() {
        return this.optionGroups.map(group => group.querySelector('select')?.value || group.querySelector('input:checked')?.value);
      }

      async initializeGarrConfigurations() {
        this.garrRows = await ttLoadGarrConfigurationRows(this.dataset.garrConfigurations);
        if (!this.isConnected) return;
        const variant = JSON.parse(this.querySelector('[data-selected-variant]')?.textContent || 'null');
        const row = this.garrRows.find(item => item.id === String(variant?.id));
        if (!row) throw new Error('Selected GARR variant is missing from configuration data');
        this.removeAttribute('aria-invalid');
        if (this.garrRevision) return;
        const mismatched = this.optionValues.some((value, index) => value !== row.options[index]);
        this.optionGroups.forEach((group,index)=>{group.dataset.garrAxis=index; if(index>=2)group.style.gridColumn="1 / -1";});
        this.applyGarrConfiguration(row);
        if (mismatched) {
          const control = this.querySelector('select, input:checked');
          control?.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      applyGarrConfiguration(row, freshAvailability = true) {
        const groups = this.optionGroups;
        groups.forEach(group => {
          group.querySelector('[data-garr-geometry]')?.remove();
          const select = group.querySelector('select');
          if (select && !select.garrAllOptions) select.garrAllOptions = Array.from(select.options);
          if (select) select.replaceChildren(...select.garrAllOptions);
        });
        groups.forEach((group, index) => {
          const select = group.querySelector('select');
          if (select) {
            select.value = row.options[index];
            Array.from(select.options).forEach(option => option.toggleAttribute('selected', option.selected));
          } else {
            group.querySelectorAll('input[type="radio"]').forEach(input => input.checked = input.value === row.options[index]);
          }
          const summary = group.querySelector('[data-selected-swatch-value]');
          if (summary) summary.textContent = row.options[index];
        });
        groups.forEach((group, index) => {
          const matching = this.garrRows.filter(item => item.options.slice(0, index).every((value, previous) => value === row.options[previous]));
          group.querySelectorAll('option, input[type="radio"]').forEach(control => {
            const exists = matching.some(item => item.options[index] === control.value);
            if (control.tagName === 'OPTION') {
              if (!exists) { control.remove(); return; }
              control.hidden = false;
              control.disabled = false;
              const soldOut = freshAvailability && exists && control.dataset.garrAvailable === 'false';
              const details = control.value.split(' · ').filter(part => !part.endsWith(': -')).filter(part => {
                const key = part.split(':')[0];
                return matching.some(item => item.options[index].split(' · ').find(detail => detail.startsWith(key + ':')) !== part);
              });
              const exact = matching.find(item => item.options[index] === control.value);
              control.textContent = (control.value.includes(' · ') ? details.join(' · ') + (details.length ? ' · ' : '') + 'EDP ' + exact.sku : control.value) + (soldOut ? ' — ' + this.dataset.garrSoldOut : '');
            } else {
              control.disabled = !exists;
              const label = Array.from(group.querySelectorAll('label')).find(label => label.htmlFor === control.id);
              control.hidden = !exists;
              control.style.display = exists ? '' : 'none';
              if (label) {
                label.hidden = !exists;
                label.style.display = exists ? '' : 'none';
              }
            }
          });
        });
        groups.forEach((group, index) => {
          const select = group.querySelector('select');
          if (!select) return;
          if (select.value.includes(' · ')) { this.renderGarrAttributes(group, select, row, index); return; }
          const numeric = value => {
            const mixed = value.match(/^(\d+)-(\d+)\/(\d+)$/);
            const fraction = value.match(/^(\d+)\/(\d+)$/);
            return mixed ? Number(mixed[1]) + Number(mixed[2]) / Number(mixed[3]) : fraction ? Number(fraction[1]) / Number(fraction[2]) : Number.parseFloat(value);
          };
          if (Array.from(select.options).every(option => Number.isFinite(numeric(option.value)))) {
            const selected = select.value;
            select.replaceChildren(...Array.from(select.options).sort((a,b) => numeric(a.value)-numeric(b.value)));
            select.value = selected;
          }
          let choices = group.querySelector('[data-garr-short-choices]');
          if (choices) choices.remove();
          const shortList = false;
          const wrapper = select.closest('.select');
          if (wrapper) wrapper.style.display = shortList ? 'none' : '';
          if (shortList) {
            choices = document.createElement('div'); choices.dataset.garrShortChoices = '';
            choices.setAttribute('role','group');
            choices.setAttribute('aria-label',group.querySelector('.font-body-bolder')?.textContent || 'Tool options');
            choices.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px';
            Array.from(select.options).forEach(option => {
              const button = document.createElement('button'); button.type = 'button';
              button.textContent = option.textContent;
              button.setAttribute('aria-pressed',String(option.selected));
              button.style.cssText = `min-height:44px;padding:10px 16px;border:1px solid ${option.selected ? '#171717' : '#d9d9dc'};border-radius:24px;background:${option.selected ? '#171717' : '#fff'};color:${option.selected ? '#fff' : '#171717'};font:inherit;line-height:1.4;text-align:left;max-width:100%;white-space:normal`;
              button.addEventListener('click',() => { select.value = option.value; select.dispatchEvent(new Event('change',{bubbles:true})); });
              choices.append(button);
            });
            group.append(choices);
          }
          const summary = group.querySelector('[data-selected-swatch-value]');
          if (summary && select.value.includes(' · ')) {
            summary.hidden = true;
            let detailList = group.querySelector('[data-garr-dimensions]');
            if (!detailList) {
              detailList = document.createElement('dl');
              detailList.dataset.garrDimensions = '';
              detailList.style.cssText = 'display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px 16px;margin:12px 0 0;padding:12px;background:white;border:1px solid #e5e5e5;border-radius:12px;font-size:14px';
              group.append(detailList);
            }
            detailList.replaceChildren(...select.value.split(' · ').filter(part => !part.endsWith(': -')).map(part => {
              const [key,value] = part.split(': ');
              const item = document.createElement('div');
              const term = document.createElement('dt');
              const definition = document.createElement('dd');
              term.textContent = key; term.style.fontWeight = '600';
              definition.textContent = value; definition.style.margin = '0';
              item.append(term,definition); return item;
            }));
          }
        });
      }

      renderGarrAttributes(group, select, row, index) {
        group.querySelector('[data-garr-short-choices]')?.remove();
        group.querySelector('[data-garr-dimensions]')?.remove();
        const wrapper = select.closest('.select');
        if (wrapper) wrapper.style.display = 'none';
        const summary = group.querySelector('[data-selected-swatch-value]');
        if (summary) summary.hidden = true;
        const heading = group.querySelector('.font-body-bolder');
        if (heading) heading.parentElement.style.display = 'none';
        group.querySelector('[data-garr-geometry]')?.remove();
        const panel = document.createElement('div'); panel.dataset.garrGeometry = '';
        panel.style.cssText = 'display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px';
        const parse = item => Object.fromEntries(item.options[index].split(' · ').map(part => { const split = part.indexOf(': '); return [part.slice(0,split),part.slice(split+2)]; }));
        let candidates = this.garrRows.filter(item => item.options.slice(0,index).every((value,i) => value === row.options[i]));
        const selected = parse(row);
        const keys = Object.keys(selected).sort((a,b) => ['Corner radius','Overall length','Reach','Shank diameter','Neck diameter','Neck length','Shank flat'].indexOf(a)-['Corner radius','Overall length','Reach','Shank diameter','Neck diameter','Neck length','Shank flat'].indexOf(b));
        keys.forEach((key,keyIndex) => {
          const values = [...new Set(candidates.map(item => parse(item)[key]))];
          values.sort((a,b) => a.localeCompare(b,undefined,{numeric:true}));
          if (values.every(value => value === '-')) return;
          const field = document.createElement('div');
          const label = document.createElement('label'); label.textContent = key;
          label.style.cssText = 'display:block;font-weight:600;margin-bottom:8px'; field.append(label);
          const choose = value => {
            const matching = this.garrRows.filter(item => item.options.slice(0,index).every((option,i) => option === row.options[i]) && keys.slice(0,keyIndex).every(previous => parse(item)[previous] === selected[previous]) && parse(item)[key] === value);
            const exact = matching.reduce((best,item) => { const data = parse(item); const score = keys.reduce((sum,k) => sum + (data[k] === selected[k] ? 1 : 0),0); return !best || score > best.score ? {item,score} : best; },null)?.item;
            if (!exact) return;
            select.value = exact.options[index]; select.dispatchEvent(new Event('change',{bubbles:true}));
          };
          if (values.length === 1) {
            const text = document.createElement('span'); text.textContent = values[0] === '-' ? 'Not specified' : values[0]; field.append(text);
          } else {
            const control=document.createElement('select'); control.dataset.garrAttribute=''; control.id=`${this.id}-geometry-${keyIndex}`; label.htmlFor=control.id;
            control.style.cssText='width:100%;min-height:44px;padding:12px;border:1px solid #d9d9dc;border-radius:12px;background:white;font:inherit';
            values.forEach(value=>{const option=document.createElement('option');option.value=value;option.textContent=value==='-'?'Not specified':value;option.selected=value===selected[key];control.append(option);});
            control.addEventListener('change',event=>{event.stopPropagation();choose(control.value);}); field.append(control);
          }
          panel.append(field);
          candidates=candidates.filter(item=>parse(item)[key]===selected[key]);
        });
        group.append(panel);
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options).forEach(option => option.removeAttribute('selected'));
          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }
    }
  );
}
